// frontend/src/pages/AboutPage.tsx
import { useState, useEffect } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building, Target, Users, Zap, TrendingUp, ShieldCheck, Lightbulb, Loader2 } from 'lucide-react';
import { getPublicAboutUsContent } from '@/api/settingService';
import { type PublicAboutUsContentData, type TeamMember, type CoreValue } from '@/types';

const backendBaseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '') || 'http://localhost:8000';

const iconMap: { [key: string]: React.FC<React.ComponentProps<typeof Building>> } = {
  Lightbulb,
  ShieldCheck,
  TrendingUp,
  Zap,
  Target,
  Users,
  Building,
};

const defaultAboutContent: PublicAboutUsContentData = {
  about_us_page_title: "About Our Company",
  about_us_page_subtitle: "Pioneering the future through innovation, integrity, and insight.",
  about_us_mission_title: "Our Mission",
  about_us_mission_p1: "To empower individuals and businesses with superior tools and actionable intelligence for long-term success.",
  about_us_mission_p2: "We are committed to demystifying complex landscapes and providing access to valuable insights.",
  about_us_mission_image_url: "https://placehold.co/600x400/2a3045/ffffff?text=Strategic+Vision", 
  about_us_story_title: "Our Story",
  about_us_story_content: "<p>Founded with a vision to redefine our industry, our company emerged from a collective of seasoned experts and innovators. We recognized a critical gap where traditional approaches were slow to adapt.</p><p>Our journey began with a commitment to rigorous research and developing proprietary solutions. Over the years, we've grown into a trusted partner, dedicated to innovation, transparency, and client-centricity.</p>",
  about_us_values_title: "Our Core Values",
  core_values: [
    { title: 'Innovation', icon: 'Lightbulb', color_class: 'text-primary', description: 'We relentlessly pursue new ideas and technologies.' },
    { title: 'Integrity', icon: 'ShieldCheck', color_class: 'text-accent', description: 'We operate with unwavering ethical standards and transparency.' },
    { title: 'Excellence', icon: 'TrendingUp', color_class: 'text-vedasMaroon-600 dark:text-vedasMaroon-500', description: 'We strive for the highest quality in all we do.' }
  ],
  about_us_team_title: "Meet Our Leadership",
  team_members: [
    { name: 'Alex Morgan', role: 'Founder & Chief Strategist', imageUrl: 'https://placehold.co/400x400/6A040F/FEF2F2?text=AM&font=roboto', bio: 'Alex leads with a vision to democratize access to sophisticated strategies.' },
    { name: 'Jordan Lee', role: 'Head of Research & Development', imageUrl: 'https://placehold.co/400x400/B38B00/FFFFFF?text=JL&font=roboto', bio: 'Jordan spearheads research, focusing on AI-driven predictive modeling.' },
    { name: 'Casey Diaz', role: 'Chief Technology Officer', imageUrl: 'https://placehold.co/400x400/50030B/FEE2E2?text=CD&font=roboto', bio: 'Casey oversees the technological infrastructure, ensuring robust platforms.' },
  ]
};


const AboutPage = () => {
  const [content, setContent] = useState<PublicAboutUsContentData>(defaultAboutContent); 
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchContent = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const fetchedData = await getPublicAboutUsContent();
        
        const mergedContent: PublicAboutUsContentData = {
          about_us_page_title: fetchedData.about_us_page_title ?? defaultAboutContent.about_us_page_title,
          about_us_page_subtitle: fetchedData.about_us_page_subtitle ?? defaultAboutContent.about_us_page_subtitle,
          about_us_mission_title: fetchedData.about_us_mission_title ?? defaultAboutContent.about_us_mission_title,
          about_us_mission_p1: fetchedData.about_us_mission_p1 ?? defaultAboutContent.about_us_mission_p1,
          about_us_mission_p2: fetchedData.about_us_mission_p2 ?? defaultAboutContent.about_us_mission_p2,
          about_us_mission_image_url: fetchedData.about_us_mission_image_url ?? defaultAboutContent.about_us_mission_image_url,
          about_us_story_title: fetchedData.about_us_story_title ?? defaultAboutContent.about_us_story_title,
          about_us_story_content: fetchedData.about_us_story_content ?? defaultAboutContent.about_us_story_content,
          about_us_values_title: fetchedData.about_us_values_title ?? defaultAboutContent.about_us_values_title,
          core_values: (fetchedData.core_values && fetchedData.core_values.length > 0) 
                          ? fetchedData.core_values 
                          : defaultAboutContent.core_values,
          about_us_team_title: fetchedData.about_us_team_title ?? defaultAboutContent.about_us_team_title,
          team_members: (fetchedData.team_members && fetchedData.team_members.length > 0) 
                            ? fetchedData.team_members 
                            : defaultAboutContent.team_members,
        };
        setContent(mergedContent);

      } catch (err) {
        console.error("Failed to fetch About Us content:", err);
        setError("Failed to load page content. Displaying default information.");
        setContent(defaultAboutContent); 
      } finally {
        setIsLoading(false);
      }
    };
    fetchContent();
  }, []);

  const getFullImageUrl = (relativePath: string | null | undefined): string | undefined => {
    if (!relativePath) return undefined;
    if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
      return relativePath;
    }
    const cleanRelativePath = relativePath.startsWith('/') ? relativePath.substring(1) : relativePath;
    if (cleanRelativePath.startsWith('static/')) {
        return `${backendBaseUrl}/${cleanRelativePath}`;
    }
    return `${backendBaseUrl}/static/${cleanRelativePath}`;
  };

  const currentContent = content; 

  if (isLoading) { 
    return (
      <div className="container mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-lg text-muted-foreground">Loading About Us page...</p>
      </div>
    );
  }

  const renderHTML = (htmlString?: string | null) => {
    if (!htmlString) return null;
    return <div dangerouslySetInnerHTML={{ __html: htmlString }} />;
  };


  return (
    <div className="container mx-auto px-4 py-8 md:py-12 space-y-16 md:space-y-24 text-foreground bg-background">
      <header className="text-center border-b border-border/60 pb-10 relative">
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-2/3 md:w-1/3 h-px bg-gradient-to-r from-transparent via-primary/70 to-transparent"></div>
        <Building className="h-16 w-16 text-primary mx-auto mb-4" />
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
          {currentContent.about_us_page_title}
        </h1>
        <p className="mt-4 text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
          {currentContent.about_us_page_subtitle}
        </p>
      </header>

      <section id="mission" className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
        <div className="order-2 md:order-1">
          <Target className="h-12 w-12 text-primary mb-4" />
          <h2 className="text-3xl md:text-4xl font-semibold mb-5 text-foreground">
            {currentContent.about_us_mission_title}
          </h2>
          {currentContent.about_us_mission_p1 &&
            <p className="text-muted-foreground text-lg mb-4 leading-relaxed">
              {currentContent.about_us_mission_p1}
            </p>
          }
          {currentContent.about_us_mission_p2 &&
            <p className="text-muted-foreground text-lg leading-relaxed">
              {currentContent.about_us_mission_p2}
            </p>
          }
        </div>
        <div className="order-1 md:order-2 rounded-xl overflow-hidden shadow-2xl border border-border/30 aspect-video">
          <img
            src={getFullImageUrl(currentContent.about_us_mission_image_url) || "https://placehold.co/600x400/7F1D1D/FFFFFF?text=Mission+Image+Unavailable"}
            alt="Our Mission - Strategic Vision"
            className="w-full h-full object-cover"
            onError={(e) => { (e.target as HTMLImageElement).src = "https://placehold.co/600x400/7F1D1D/FFFFFF?text=Mission+Image+Error"; }}
          />
        </div>
      </section>

      {currentContent.about_us_story_content && (
        <section id="story" className="bg-gradient-to-br from-muted/40 via-background to-muted/60 dark:from-muted/10 dark:via-background dark:to-muted/20 p-8 md:p-12 rounded-xl shadow-lg border border-border/20">
          <Zap className="h-12 w-12 text-accent mb-4" />
          <h2 className="text-3xl md:text-4xl font-semibold mb-6 text-foreground">
            {currentContent.about_us_story_title}
          </h2>
          <div className="space-y-5 text-muted-foreground text-lg leading-relaxed prose prose-lg max-w-none prose-p:text-inherit">
            {renderHTML(currentContent.about_us_story_content)}
          </div>
        </section>
      )}

      {(currentContent.core_values && currentContent.core_values.length > 0) && (
        <section id="values">
          <h2 className="text-3xl md:text-4xl font-semibold mb-10 md:mb-12 text-center text-gradient-maroon-to-gold">
            {currentContent.about_us_values_title}
          </h2>
          <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-8">
            {currentContent.core_values.map((value: CoreValue) => {
              const ValueIcon = iconMap[value.icon] || Lightbulb; 
              return (
                <Card key={value.title} className="group bg-card text-card-foreground text-center shadow-lg hover:shadow-xl dark:hover:shadow-primary/10 transition-all duration-300 ease-in-out border border-border/30 hover:border-primary/30 relative overflow-hidden transform hover:-translate-y-1">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-accent/80 via-primary/80 to-accent/80 scale-x-0 group-hover:scale-x-100 transition-transform duration-400 ease-out origin-center"></div>
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400 bg-gradient-to-br from-primary/5 via-background to-accent/5 dark:from-primary/10 dark:via-card dark:to-accent/10"></div>
                  <div className="relative z-10">
                    <CardHeader className="pt-8 pb-4">
                      <ValueIcon className={`h-10 w-10 ${value.color_class || 'text-primary'} mx-auto mb-3 group-hover:scale-110 transition-transform`} />
                      <CardTitle className="text-2xl text-foreground group-hover:text-primary transition-colors">{value.title}</CardTitle>
                    </CardHeader>
                    <CardContent className="pb-8">
                      <p className="text-muted-foreground leading-relaxed">{value.description}</p>
                    </CardContent>
                  </div>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {(currentContent.team_members && currentContent.team_members.length > 0) && (
        <section id="team" className="pt-8 md:pt-12">
          <div className="text-center mb-10 md:mb-12">
              <Users className="h-12 w-12 text-primary mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-semibold text-gradient-maroon-to-gold">
                {currentContent.about_us_team_title}
              </h2>
          </div>
          <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-8">
            {currentContent.team_members.map((member: TeamMember) => (
              <Card key={member.name} className="group bg-card text-card-foreground text-center overflow-hidden shadow-lg hover:shadow-xl dark:hover:shadow-primary/10 transition-all duration-300 ease-out transform hover:scale-105 border border-border/30 hover:border-primary/30">
                <div className="bg-gradient-to-br from-muted/50 to-primary/10 dark:from-muted/20 dark:to-primary/5 p-6 relative">
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-20 dark:group-hover:opacity-30 transition-opacity duration-400 bg-gradient-to-tr from-accent via-primary to-accent mix-blend-multiply"></div>
                  <Avatar className="w-32 h-32 mx-auto mb-4 border-4 border-primary/50 group-hover:border-accent shadow-lg transition-colors">
                    <AvatarImage 
                        src={getFullImageUrl(member.imageUrl) || `https://placehold.co/400x400/cccccc/333333?text=${member.name.split(' ').map((n: string) => n[0]).join('')}`} 
                        alt={member.name} 
                        onError={(e) => { (e.target as HTMLImageElement).src = `https://placehold.co/400x400/cccccc/333333?text=${member.name.split(' ').map((n: string) => n[0]).join('')}&font=roboto`; }}
                    />
                    <AvatarFallback className="bg-primary/10 text-primary text-3xl font-semibold group-hover:bg-accent/10 group-hover:text-accent transition-colors">
                      {member.name.split(' ').map((n: string) => n[0]).join('').toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </div>
                <CardHeader className="pt-6 pb-2">
                  <CardTitle className="text-xl text-foreground group-hover:text-primary transition-colors">{member.name}</CardTitle>
                  <p className="text-primary dark:text-accent font-medium group-hover:font-semibold transition-all">{member.role}</p>
                </CardHeader>
                <CardContent className="pb-6">
                  <p className="text-sm text-muted-foreground leading-relaxed">{member.bio}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {error && <div className="text-center text-red-500 my-8">{error}</div>}
    </div>
  );
};

export default AboutPage;
